import { gReactive } from '@gradin/vue-utils'
import { computed, ref } from 'vue'
import { useApi } from '@/functions/api'

export function useProductList () {
  const api = useApi()
  const products = ref<Product[]>([])
  const pagination = ref<Pagination>()
  const loading = ref(false)

  const getData = async (params: object) => {
    try {
      loading.value = true
      const response = await api.GET<ApiCollection<Product>>('warehouse/product', params)
      products.value = response.data
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  return {
    getData,
    loading,
    products,
    pagination,
  }
}

export function useProductUpdate () {
  const api = useApi()
  const form = gReactive({
    name: '',
    price: 0,
    code: '',
    product_group_id: null as number | null,
  })
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const product = ref<Product>()

  const submitForm = async (id: number) => {
    submitting.value = true
    try {
      const response = api.PUT<Product>(`warehouse/product/${id}`, form)
      product.value = await response
    } catch (error) {
      errors.value = api.formErrors(error)
    } finally {
      submitting.value = false
    }
  }

  return {
    form,
    submitForm,
    submitting,
    product,
    errors,
  }
}

export function useProductResetRetailReady () {
  const api = useApi()
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const form = gReactive({
    products: [] as number[],
  })

  const submitForm = async () => {
    submitting.value = true
    errors.value = {}
    try {
      await api.PUT('warehouse/product/reset-retail-ready', form)
      return true
    } catch (error) {
      errors.value = api.formErrors(error)
      return false
    } finally {
      submitting.value = false
    }
  }

  return {
    form,
    submitting,
    errors,
    submitForm,
  }
}

export function useProductSetPrice () {
  const api = useApi()
  const submitting = ref(false)
  const errors = ref<FormError>({})
  const form = gReactive({
    products: [] as Array<{
      id: number,
      price: number,
    }>,
  })

  const submitForm = async () => {
    submitting.value = true
    errors.value = {}
    try {
      await api.PUT('warehouse/product/set-price', form)
      return true
    } catch (error) {
      errors.value = api.formErrors(error)
      return false
    } finally {
      submitting.value = false
    }
  }

  return {
    form,
    submitting,
    errors,
    submitForm,
  }
}

export function useProductVariantFinder () {
  const api = useApi()
  const loading = ref(false)

  const getProductVariant = async (code: string) => {
    const response = await api.GET<ApiCollection<ProductVariantSearchResource>>(`warehouse/product-variant/${encodeURIComponent(code)}`)
    return response.data
  }

  return {
    loading,
    getProductVariant,
  }
}

export function useProductStockCheck () {
  const api = useApi()
  const loading = ref(false)
  const stockData = ref<ProductStockResponse | null>()

  const getStockByCode = async (code: string) => {
    stockData.value = null

    if (!code) {
      return
    }

    try {
      loading.value = true
      const response = await api.GET<ProductStockResponse>(`warehouse/product/stock/${code}`)
      stockData.value = response
    } catch {
      stockData.value = null
    } finally {
      loading.value = false
    }
  }

  const variants = computed(() => {
    return stockData.value?.data.map(variant => variant.variant) ?? []
  })
  const maxStock = computed(() => {
    const stocks = stockData.value?.data.flatMap(variant =>
      variant.stocks.map(stock => stock.stock),
    ) ?? []

    return Math.max(...stocks, 1) // Ensure at least 1 to avoid division by zero
  })

  const getHeatmapStyle = (stock: number) => {
    if (stock === 0) {
      return {
        backgroundColor: 'white',
      }
    }
    let opacity = stock / maxStock.value
    opacity = 0.1 + opacity * 0.9
    return {
      backgroundColor: `oklch(from var(--color-primary-500) l c h / ${opacity.toFixed(2)})`,
    }
  }

  return {
    loading,
    variants,
    stockData,
    getStockByCode,
    getHeatmapStyle,
  }
}

export function usePrintBarcodeProductList () {
  const api = useApi()
  const products = ref<ProductPrintBarcode[]>()
  const pagination = ref<Pagination>()
  const loading = ref(false)

  const getData = async (params = {}) => {
    try {
      loading.value = true
      const response = await api.GET<ApiCollection<ProductPrintBarcode>>('warehouse/print-barcode/product', params)
      products.value = response.data
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  return {
    getData,
    loading,
    products,
    pagination,
  }
}

export function usePrintBarcodePurchaseList () {
  const api = useApi()
  const purchases = ref<PurchaseDraftPrintBarcode[]>()
  const pagination = ref<Pagination>()
  const loading = ref(false)

  const getData = async (params = {}) => {
    try {
      loading.value = true
      const response = await api.GET<ApiCollection<PurchaseDraftPrintBarcode>>('warehouse/print-barcode/purchase', params)
      purchases.value = response.data.filter(p => p.products.length > 0)
      pagination.value = response.meta
    } finally {
      loading.value = false
    }
  }

  return {
    getData,
    loading,
    purchases,
    pagination,
  }
}

export function useProductStockCard () {
  const api = useApi()
  const pagination = ref<Pagination>()
  const loading = ref(false)
  const stockCardData = ref<ProductStockCardResponse | null>(null)

  const loadData = async (code: string, params = {}) => {
    stockCardData.value = null

    if (!code) {
      return
    }

    try {
      loading.value = true
      const response = await api.GET<ProductStockCardResponse>(`warehouse/product/stock-card/${code}`, params)
      stockCardData.value = response
      pagination.value = response.meta
    } catch {
      stockCardData.value = null
    } finally {
      loading.value = false
    }
  }

  return {
    loading,
    stockCardData,
    pagination,
    loadData,
  }
}
